<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 text-gray-800 transition-colors duration-300">
    <!-- 现代风格背景 -->
    <div class="tech-background">
      <!-- 移除矩阵雨效果，使用Tailwind实现的背景 -->
      <div class="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-100"></div>
      <div class="grid-overlay"></div>
      <div class="floating-particles"></div>
      <div class="light-beam"></div>
    </div>
    
    <router-view v-slot="{ Component }" :key="$route.fullPath">
      <transition
        name="fade"
        mode="out-in"
      >
        <keep-alive>
          <component :is="Component" />
        </keep-alive>
      </transition>
    </router-view>
    <LoginModal v-model="showLoginModal" />
  </div>
</template>

<script setup lang="ts">
// 添加全局Window接口扩展声明
declare global {
  interface Window {
    initTechBackground?: () => void;
    cleanupTechBackground?: () => void;
  }
}

import { onMounted, onBeforeMount, onBeforeUnmount, nextTick, ref, provide } from 'vue';
import { useUserStore } from '@/stores/user';
import { getDeviceFingerprint } from '@/utils/fingerprint';
import LoginModal from '@/components/LoginModal.vue';

// 仅导入，不立即执行
import '@/assets/tech-background.js';

const userStore = useUserStore();
const showLoginModal = ref(false);
// 用于跟踪是否已经初始化过数据
const hasInitializedData = ref(false);

provide('openLoginModal', () => {
  showLoginModal.value = true;
});

// 移除深色主题设置
document.documentElement.classList.remove('dark');

onMounted(() => {
  // 确保组件挂载后初始化背景效果
  if (window.initTechBackground) {
    window.initTechBackground();
  }
});

onBeforeUnmount(() => {
  // 组件卸载前清理背景效果
  if (window.cleanupTechBackground) {
    window.cleanupTechBackground();
  }
  
  // 清理本地存储中的初始化标志
  localStorage.removeItem('app_data_initializing');
});

onBeforeMount(async () => {
  // 获取设备指纹
  const deviceId = await getDeviceFingerprint();
});

// 集中初始化所有应用数据
const initializeAppData = async () => {
  // 如果已经初始化过，则不再重复初始化
  if (hasInitializedData.value) {
    return;
  }
    
  try {
    localStorage.setItem('app_data_initializing', 'true');
    
    // 并行加载所有需要的数据
    await Promise.all([
      userStore.fetchUserInfo(),
    ]);
    
    // 设置已初始化标志
    hasInitializedData.value = true;
    
    // 移除加载状态标志
    localStorage.removeItem('app_data_initializing');
  } catch (error) {
    localStorage.removeItem('app_data_initializing');
  }
};

// 恢复用户会话 - 在DOM加载完成后执行
onMounted(async () => {  
  // 使用nextTick确保DOM完全加载后再进行认证检查
  nextTick(async () => {
    if (userStore.isAuthenticated && !hasInitializedData.value) {
      await initializeAppData();
    }
  });
});

</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 应用全局透明背景样式 */
.bg-transparent {
  background-color: transparent !important;
}

/* 修改网格线样式 */
.grid-overlay {
  background-image: 
    linear-gradient(rgba(99, 179, 237, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(99, 179, 237, 0.05) 1px, transparent 1px) !important;
  background-size: 40px 40px !important;
}

/* 修改浮动粒子样式 */
.floating-particles {
  opacity: 0.1 !important;
  filter: blur(1px) !important;
}

/* 修改光束效果 */
.light-beam {
  background: radial-gradient(
    ellipse at center,
    rgba(99, 179, 237, 0.05) 0%,
    rgba(99, 179, 237, 0) 70%
  ) !important;
}

/* 现代主题的卡片样式 */
.card {
  @apply bg-white/85 backdrop-blur-lg border border-gray-200 rounded-xl shadow-md hover:shadow-lg transition-all duration-300;
}

/* 渐变文本 */
.gradient-text {
  @apply bg-gradient-to-r from-blue-500 to-indigo-500 bg-clip-text text-transparent;
}
</style>
